import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Rating, Button } from '@mui/material';
import { CallRatingRequestPayload, makeRatingCall } from '../../services/call-rating.service';
import useUser from '../../hooks/useUser';
import UserAvatar from '../common/UserAvatar';
import { useCall } from '../../customer-call-sdk';
import StarIcon from '@mui/icons-material/Star';

const StyledCustomerRating = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    overflow: auto;
    flex-direction: column;
    justify-content: flex-start;
    padding: 1.2rem;
    font-family: sans-serif;
    .header-style {
        font-weight: 700;
        text-align: center;
        font-size: 2.6rem;
    }

    .avatar-container {
        display: block;
        align-self: center;
        margin-bottom: 10px;
        .avatar {
            width: 130px;
            height: 130px;
            color: #bac0c6;
            background-color: #bac0c6;
        }
        .avatar svg {
            width: 100px;
            height: 100px;
            color: black;
        }
    }

    .interpreter-name {
        font-weight: 600;
        text-align: center;
        font-size: 1.25rem;
        color: var(--text-primary);
        margin-bottom: 0.25rem;
    }

    .NRPCD-register-number {
        margin-top: -10px;
        font-weight: 200;
        font-size: 1.3rem;
        text-align: center;
        color: #1c1c1c;
    }

    .call-duration {
        font-weight: 200;
        text-align: center;
        font-size: 1rem;
        color: #1c1c1c;
        margin-top: 10px;
        margin-bottom: 15px;
    }

    .Horizontal-line {
        width: 100%;
        border: 1px solid #efefef;
    }

    .rate-div-box {
        display: block;
        align-self: center;
        margin-bottom: 1.7rem;
        margin-top: 1.7rem;
    }

    .button-area-box {
        padding: 20px;
        margin-top: 100px;
    }

    .rate-button-style {
        width: 100%;
        background-color: #00934a;
        border-radius: 300px;
        color: white;
        line-height: 40px;
        text-align: center;
        margin-bottom: 1rem;
        font-weight: bold;
    }
    Button:disabled {
        background-color: #bce3cd;
    }

    .skip-button-style {
        color: black;
        display: flex;
        justify-content: center;
        margin-top: 10px;
        cursor: pointer;
        font-weight: bold;
        font-size: 1.2rem;
    }

    .text-area {
    }
`;

const StyledRating = styled(Rating)`
    color: #00934a;
    font-size: 4.5rem;
`;

export interface CustomerRatingProps extends React.HTMLAttributes<HTMLDivElement> {}

const CustomerRating: React.FC<CustomerRatingProps> = ({ ...props }) => {
    const user = useUser();
    const call = useCall();
    const { ratingDetail } = call;
    const [rating, setRating] = useState<number>(0);
    const [isRating, setIsRating] = useState<boolean>(false);

    const createRating = async () => {
        const ratingDetailForPayload = Object.create(ratingDetail);
        call.closeRating();
        if (ratingDetailForPayload) {
            const payload: CallRatingRequestPayload = {
                userId: user?.uuid as string,
                requestId: ratingDetailForPayload.requestId as string,
                rating,
                comment: '',
                callQualityComment: '',
                callQualityRating: rating,
                providerId: ratingDetailForPayload.providerName as string,
            };
            await makeRatingCall(payload);
        }
    };

    const skipRating = () => {
        call.closeRating();
    };

    const getAriaLabel = (value: number | null) => {
        if (value === null || value === 0) return 'Star rating';
        return `${value} star${value > 1 ? 's' : ''}`;
    };

    useEffect(() => {
        document.querySelectorAll('.MuiRating-label')?.forEach((ele, i) => {
            ele.setAttribute('aria-label', getAriaLabel(i + 1));
        });
    }, []);

    return (
        <StyledCustomerRating {...props}>
            <h1 className="header-style" aria-label="How was your call experience?">
                How was your call experience?
            </h1>

            <div className="avatar-container">
                <UserAvatar className="avatar" />
            </div>

            <div className="text-area">
                <div className="interpreter-name">{ratingDetail?.interpreterName}</div>
                <div className="NRPCD-register-number">{ratingDetail?.interpreterNRPCDNumber}</div>
                <div className="call-duration">{`${ratingDetail?.callDuration} minutes today`}</div>
            </div>

            <div className="Horizontal-line" />
            <div className="rate-div-box">
                <StyledRating
                    precision={1}
                    size="large"
                    max={5}
                    onChange={(event, value) => {
                        setRating(value as number);
                        if ((value as number) > 0) {
                            setIsRating(true);
                        } else {
                            setIsRating(false);
                        }
                    }}
                    aria-label={getAriaLabel(rating)}
                    emptyIcon={<StarIcon fontSize="inherit" />}
                />
            </div>
            <div className="button-area-box">
                <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    onClick={createRating}
                    className="rate-button-style"
                    disabled={!isRating}
                    aria-label="Submit Rating"
                >
                    Rate
                </Button>

                <div
                    className="skip-button-style"
                    onClick={skipRating}
                    role="button"
                    aria-label="Skip button"
                >
                    Skip
                </div>
            </div>
        </StyledCustomerRating>
    );
};

export default CustomerRating;
